import {Component, inject, signal} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormMode} from '../../../shared/types/common';
import {DrawerFormBase} from '../drawer-form-base';
import {ManagementStore} from '../management.store';
import {ManagementService} from '../management.service';
import {AuthorizationService} from '../../../services/authorization.service';
import {Permissions} from '../../../shared/permissions';
import {CustomValidators} from '../../../shared/validators';
import {AiPrompt, AiTopicRow, PromptResultItem} from '../types';
import {formatDateToLocalISO, getLastNumDays, getManagementUtcDateFormat} from '../../../utils/time';
import {sentimentLabel} from '../../../utils/sentiment';
import {DryRunModalComponent} from './dry-run-modal.component';
import {ConfirmTypeToDeleteModalComponent} from '../../../shared/components/confirm-type-to-delete-modal/confirm-type-to-delete-modal.component';
import {TitledInputComponent} from '../../../shared/components/titled-input/titled-input.component';
import {DeleteBtnComponent} from '../../../shared/components/delete-btn/delete-btn.component';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {LoaderComponent} from '../../../shared/components/loader/loader.component';
import {DatePickerComponent, FixedDateRanges} from '../../../shared/components/date-picker/date-picker.component';

const RESULTS_PAGE_SIZE = 25;

/**
 * AI Presence topic drawer (DEV-695). Add mode creates the topic; edit mode also manages the topic's
 * prompts (bulk add, test-run, delete) and previews its collected results.
 */
@Component({
  selector: 'app-topic-drawer',
  imports: [ReactiveFormsModule, TitledInputComponent, ButtonComponent, LoaderComponent, DatePickerComponent, DeleteBtnComponent],
  templateUrl: './topic-drawer.component.html',
  styleUrl: './topic-drawer.component.scss',
})
export class TopicDrawerComponent extends DrawerFormBase<AiTopicRow> {
  private readonly store = inject(ManagementStore);
  private readonly api = inject(ManagementService);
  private readonly authorization = inject(AuthorizationService);

  protected readonly canWrite = this.authorization.hasPermissions([Permissions.WRITE_AI_TOPIC]);
  // Server's DELETE /ai-topics/:id requires delete:ai-topic, not write — mirror it or the flow 403s
  protected readonly canDelete = this.authorization.hasPermissions([Permissions.DELETE_AI_TOPIC]);

  public readonly name = new FormControl(this.entity?.name ?? '', [CustomValidators.required('Topic name')]);
  public readonly form = new FormGroup({name: this.name});

  public readonly configs = {
    [FormMode.ADD]: {title: 'Add topic', submitText: 'Add'},
    [FormMode.EDIT]: {title: 'Edit topic', submitText: 'Update'},
  };

  // --- prompts (edit mode) ---
  protected readonly bulkPromptsControl = new FormControl('');
  protected readonly prompts = signal<AiPrompt[]>([]);
  protected readonly promptsLoading = signal(false);
  protected readonly promptsSaving = signal(false);

  // --- results (edit mode) ---
  protected readonly dateRangeGroup = new FormGroup({
    startDate: new FormControl<Date | null>(null),
    endDate: new FormControl<Date | null>(null),
    relativeDate: new FormControl<FixedDateRanges | undefined>(undefined, {nonNullable: true}),
  });
  protected readonly results = signal<PromptResultItem[]>([]);
  protected readonly resultsTotal = signal(0);
  protected readonly resultsLoading = signal(false);
  protected readonly resultsLoaded = signal(false);
  protected readonly expandedResults = signal<Set<number>>(new Set());
  private resultsPage = 1;
  // Bumped on every load; a late response from an OLD query (range changed mid-flight) is dropped
  private resultsRequestToken = 0;

  // "Pipeline only" rows (no console topic) can view/delete prompts and load results, but there is
  // nothing to rename, and adding prompts needs a console topic. Creating one with this name adopts them.
  protected readonly managed = this.entity?.managed !== false;

  constructor() {
    super();
    this.currentMode = this.entity ? FormMode.EDIT : FormMode.ADD;
    if (!this.canWrite || !this.managed) {
      this.form.disable();
    }
    // Yesterday by default; results load only on an explicit click — the collection queries hit
    // Snowflake through the interaction-layer proxy and must not fire on every drawer open.
    const initialRange = getLastNumDays(1);
    this.dateRangeGroup.patchValue({startDate: initialRange.startDate, endDate: initialRange.endDate, relativeDate: initialRange.relativeDate});
    if (this.entity) {
      this.loadPrompts();
    }
  }

  // Add mode only — the drawer is a form there (name -> Add). In edit mode every action
  // (rename, prompt add/delete) applies immediately through its own button; there is no
  // pending state, so the footer offers Close, not Cancel/Update.
  submit(): void {
    // A disabled form reports invalid=false — guard the unmanaged/read-only cases explicitly
    if (this.currentMode !== FormMode.ADD || this.form.invalid || this.form.disabled || this.saving()) {
      return;
    }
    this.saving.set(true);
    this.store.addAiTopic((this.name.value ?? '').trim()).subscribe({
      next: () => this.onSuccess('Topic created'),
      error: error => this.onError(error),
    });
  }

  protected nameChanged(): boolean {
    const name = (this.name.value ?? '').trim();
    return !!name && name !== this.entity?.name;
  }

  /** Inline rename — applies immediately, like every other action in the edit drawer. */
  protected rename(): void {
    if (!this.nameChanged() || this.form.invalid || this.form.disabled || this.saving()) {
      return;
    }
    this.saving.set(true);
    this.store.renameAiTopic(this.entity!.id, (this.name.value ?? '').trim()).subscribe({
      next: () => this.onSuccess('Topic renamed. Collected history keeps flowing under the new name.'),
      error: error => {
        if ((error as {status?: number})?.status === 409) {
          this.saving.set(false);
          this.snackError('This topic name is already taken');
          return;
        }
        this.onError(error);
      },
    });
  }

  deleteTopic(): void {
    const topic = this.entity!;
    if (!this.canDelete) {
      return;
    }
    if (!this.managed) {
      return; // nothing to delete — no console topic behind this row
    }
    if (topic.brandKeys.length) {
      this.utilService.openSnackBar(`Detach "${topic.name}" from its ${topic.brandKeys.length} brand(s) before deleting it`, {position: 'center', variant: 'error'});
      return;
    }
    this.confirmAndDelete(
      {
        header: 'Delete topic',
        description: `This permanently deletes "${topic.name}" and ALL its prompts, so nothing under it runs again. Answers already collected stay in results and reports.`,
        confirmLabel: 'Delete',
        expectedValue: topic.name,
      },
      () => this.store.deleteAiTopic(topic.id),
      'Topic deleted',
    );
  }

  // ---------- prompts ----------

  protected loadPrompts(): void {
    const topic = this.entity!.name.toLowerCase();
    this.promptsLoading.set(true);
    this.store
      .allAiPromptsSource()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: all => {
          this.prompts.set(all.filter(prompt => prompt.topic.toLowerCase() === topic));
          this.promptsLoading.set(false);
        },
        error: () => {
          this.promptsLoading.set(false);
          this.snackError('Failed to load prompts');
        },
      });
  }

  protected addPrompts(): void {
    const lines = this.bulkPromptLines();
    if (!lines.length) {
      this.snackError('Write one prompt per line to add');
      return;
    }
    if (this.promptsSaving()) {
      return;
    }
    this.promptsSaving.set(true);
    this.store.addAiPrompts(lines.map(promptText => ({topic: this.entity!.name, promptText}))).subscribe({
      next: created => {
        this.bulkPromptsControl.reset('');
        this.promptsSaving.set(false);
        this.loadPrompts(); // the store invalidated its cache — this refetches
        this.utilService.openSnackBar(`Added ${created.length} prompt(s) — collection starts at the next daily run`, {position: 'center', variant: 'success'});
      },
      error: error => {
        this.promptsSaving.set(false);
        this.snackError((error as {error?: {message?: string}})?.error?.message ?? 'Failed to add prompts');
      },
    });
  }

  protected deletePrompt(prompt: AiPrompt): void {
    if (!this.canDelete) {
      return;
    }
    this.dialog
      .open(ConfirmTypeToDeleteModalComponent, {
        panelClass: 'management-modal',
        autoFocus: false,
        data: {
          header: 'Delete prompt',
          description: `\u201C${prompt.promptText}\u201D stops running from tomorrow. Answers it already collected stay in results and reports. This cannot be undone.`,
          confirmLabel: 'Delete',
        },
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }
        this.saving.set(true); // lock the drawer while the delete is in flight, like deleteTopic
        this.store.deleteAiPrompt(prompt.promptId).subscribe({
          next: () => {
            this.saving.set(false);
            this.prompts.update(prompts => prompts.filter(row => row.promptId !== prompt.promptId));
            this.utilService.openSnackBar('Prompt deleted', {position: 'center', variant: 'success'});
          },
          error: error => {
            this.saving.set(false);
            this.snackError((error as {error?: {message?: string}})?.error?.message ?? 'Failed to delete prompt');
          },
        });
      });
  }

  protected testPrompt(): void {
    const lines = this.bulkPromptLines();
    if (lines.length !== 1) {
      this.snackError('Test runs a single prompt — keep exactly one line to test it');
      return;
    }
    const firstLine = lines[0];
    this.dialog.open(DryRunModalComponent, {width: '760px', maxWidth: '90vw', autoFocus: false, data: {topic: this.entity!.name, promptText: firstLine}});
  }

  // ---------- results ----------

  protected loadResults(append = false): void {
    const {startDate, endDate} = this.dateRangeGroup.value;
    if (!startDate || !endDate) {
      return;
    }
    if (this.resultsLoading()) {
      return; // one in-flight results request at a time (Load more shares this guard)
    }
    this.resultsPage = append ? this.resultsPage + 1 : 1;
    this.resultsLoading.set(true);
    const token = ++this.resultsRequestToken;
    this.api.getAiPromptResults(this.entity!.name, formatDateToLocalISO(startDate), formatDateToLocalISO(endDate), this.resultsPage, RESULTS_PAGE_SIZE).subscribe({
      next: response => {
        if (token !== this.resultsRequestToken) {
          return; // stale response from a superseded query — never mix ranges
        }
        this.results.update(current => (append ? [...current, ...response.data] : response.data));
        this.resultsTotal.set(response.total);
        this.resultsLoaded.set(true);
        if (!append) {
          this.expandedResults.set(new Set());
        }
        this.resultsLoading.set(false);
      },
      error: error => {
        if (token !== this.resultsRequestToken) {
          return;
        }
        // Roll the page back — otherwise retrying "Load more" skips the page that just failed
        if (append) {
          this.resultsPage -= 1;
        }
        this.resultsLoading.set(false);
        this.snackError((error as {error?: {message?: string}})?.error?.message ?? 'Failed to load results');
      },
    });
  }

  protected toggleExpanded(index: number): void {
    this.expandedResults.update(expanded => {
      const next = new Set(expanded);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  protected isExpanded(index: number): boolean {
    return this.expandedResults().has(index);
  }

  protected get hasMoreResults(): boolean {
    return this.results().length < this.resultsTotal();
  }

  protected formatDate(date: string): string {
    return getManagementUtcDateFormat(date);
  }

  /** Collapsed-row summary: the first matched brands as chips; the rest fold into "+N" (expand for all). */
  protected summaryBrands(item: PromptResultItem): {shown: string[]; more: number; rest: string} {
    const keys = item.mentions.map(mention => mention.brandKey);
    const shown = keys.slice(0, 3);
    return {shown, more: keys.length - shown.length, rest: keys.slice(3).join(', ')};
  }

  protected copyResult(item: PromptResultItem): void {
    navigator.clipboard.writeText(item.responseText)
      .then(() => this.utilService.openSnackBar('Copied to clipboard', {variant: 'success', position: 'center', duration: 2000}))
      .catch(() => this.snackError('Failed to copy to clipboard'));
  }

  // ---------- shared ----------

  protected bulkPromptLines(): string[] {
    return (this.bulkPromptsControl.value ?? '').split('\n').map(line => line.trim()).filter(Boolean);
  }

  protected readonly sentimentLabel = sentimentLabel;

  private snackError(message: string): void {
    this.utilService.openSnackBar(message, {position: 'center', variant: 'error'});
  }
}
